import { FormEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Search, Compass } from "lucide-react";
import { ArticleCard } from "../../components/ArticleCard";
import { EmptyState, ErrorState, LoadingState } from "../../components/LoadingState";
import { NewsletterBox } from "../../components/NewsletterBox";
import { Seo } from "../../components/Seo";
import { useApi } from "../../hooks/useApi";
import type { Article, Paged } from "../../types";

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") ?? "";
  const [query, setQuery] = useState(initial);
  const { data, loading, error } = useApi<Paged<Article>>(`/api/search?q=${encodeURIComponent(initial)}`, [initial]);

  function submit(event: FormEvent) {
    event.preventDefault();
    setParams(query.trim() ? { q: query.trim() } : {});
  }

  return (
    <>
      <Seo title={initial ? `Search: "${initial}" — ZimSmart Magazine` : "Search The Archive — ZimSmart Magazine"} />
      <div className="mag-container mag-search-page-flow">
        <header className="mag-section-banner">
          <div className="mag-banner-kicker">
            <Compass size={16} className="text-amber-600" />
            <span>ARCHIVE & ARTICLE DISCOVERY</span>
          </div>
          <h1 className="mag-banner-title">
            {initial ? `Results for “${initial}”` : "Search ZimSmart Archive"}
          </h1>
          <p className="mag-banner-sub">
            Query published investigations, market indicators, executive features, and product benchmarks across all editions.
          </p>

          <form className="mag-page-search-form" onSubmit={submit}>
            <div className="mag-page-search-input-wrap">
              <Search size={20} className="mag-search-icon" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by topic, company, keyword, or author..."
              />
            </div>
            <button type="submit" className="mag-page-search-btn">
              <span>Search Newsroom</span>
              <ArrowRight size={16} />
            </button>
          </form>
        </header>

        {loading ? (
          <LoadingState label="Searching editorial archives..." />
        ) : error ? (
          <ErrorState message={error} />
        ) : data?.items.length ? (
          <div className="mag-listing-content">
            <div className="mag-results-meta">
              <span>Found {data.items.length} matching {data.items.length === 1 ? "article" : "articles"}</span>
            </div>
            <div className="mag-articles-grid">
              {data.items.map((article) => (
                <ArticleCard key={article.id} article={article} variant="compact" />
              ))}
            </div>
          </div>
        ) : (
          <EmptyState
            title={initial ? `No articles found matching “${initial}”` : "Begin your archive search"}
            body="Try searching for broad terms like 'fintech', 'solar', 'ecocash', 'startups', or '5g'."
          />
        )}

        <NewsletterBox />
      </div>
    </>
  );
}
